import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { Layout } from './components/layout/Layout';
import { initializeCsrfToken } from './services/api';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { logger } from './utils/logger';

// Lazy load pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Containers = lazy(() => import('./pages/Containers').then(m => ({ default: m.Containers })));
const ContainerDetail = lazy(() => import('./pages/ContainerDetail').then(m => ({ default: m.ContainerDetail })));
const Customers = lazy(() => import('./pages/Customers').then(m => ({ default: m.Customers })));
const Orders = lazy(() => import('./pages/Orders').then(m => ({ default: m.Orders })));
const Invoices = lazy(() => import('./pages/Invoices').then(m => ({ default: m.Invoices })));
const Fleet = lazy(() => import('./pages/Fleet').then(m => ({ default: m.Fleet })));
const Bilaflotin = lazy(() => import('./pages/Bilaflotin').then(m => ({ default: m.Bilaflotin })));
const Alerts = lazy(() => import('./pages/Alerts').then(m => ({ default: m.Alerts })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const CMSList = lazy(() => import('./pages/cms').then(m => ({ default: m.CMSList })));
const EditSection = lazy(() => import('./pages/cms/EditSection').then(m => ({ default: m.EditSection })));
const Documentation = lazy(() => import('./pages/Documentation').then(m => ({ default: m.Documentation })));
const Markdown = lazy(() => import('./pages/Markdown').then(m => ({ default: m.Markdown })));
const Login = lazy(() => import('./pages/Login'));

// Admin pages
const UserManagement = lazy(() => import('./pages/UserManagement').then(m => ({ default: m.UserManagement })));
const FleetAssignment = lazy(() => import('./pages/FleetAssignment').then(m => ({ default: m.FleetAssignment })));
const ContainerAssignment = lazy(() => import('./pages/ContainerAssignment').then(m => ({ default: m.ContainerAssignment })));
const Feedback = lazy(() => import('./pages/Feedback').then(m => ({ default: m.Feedback })));
const HeroVideos = lazy(() => import('./pages/HeroVideos').then(m => ({ default: m.HeroVideos })));
const Analytics = lazy(() => import('./pages/Analytics').then(m => ({ default: m.Analytics })));
const RouteOptimization = lazy(() => import('./pages/RouteOptimization').then(m => ({ default: m.RouteOptimization })));
const StrategicAnalysis = lazy(() => import('./pages/StrategicAnalysis').then(m => ({ default: m.StrategicAnalysis })));
const ROICalculator = lazy(() => import('./pages/ROICalculator').then(m => ({ default: m.ROICalculator })));
const Calls = lazy(() => import('./pages/Calls'));

// PrivateRoute component - protects routes that require authentication
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

// Loading fallback component
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Hleð...</p>
      </div>
    </div>
  );
}

function App() {
  // Initialize CSRF token on app load
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      // Only initialize CSRF token if user is authenticated
      initializeCsrfToken().catch((error) => {
        logger.warn('[App] Failed to initialize CSRF token:', error);
      });
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
          <Route path="/login" element={<Login />} />

          <Route path="/" element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="gamar" element={<Containers />} />
            <Route path="gamar/:id" element={<ContainerDetail />} />
            <Route path="vidskiptavinir" element={<Customers />} />
            <Route path="vidskiptavinir/:id" element={<Customers />} />
            <Route path="pantanir" element={<Orders />} />
            <Route path="pantanir/:id" element={<Orders />} />
            <Route path="reikningar" element={<Invoices />} />
            <Route path="reikningar/:id" element={<Invoices />} />
            <Route path="bilafloti" element={<Bilaflotin />} />
            <Route path="kortayfirlit" element={<Fleet />} />
            <Route path="tilkynningar" element={<Alerts />} />
            <Route path="cms" element={<CMSList />} />
            <Route path="cms/edit/:key" element={<EditSection />} />
            <Route path="skjolun" element={<Documentation />} />
            <Route path="markdown" element={<Markdown />} />
            <Route path="stillingar" element={<Settings />} />

            {/* Admin routes */}
            <Route path="notendur" element={<UserManagement />} />
            <Route path="bilafloti-skipulag" element={<FleetAssignment />} />
            <Route path="gamar-skipulag" element={<ContainerAssignment />} />
            <Route path="leida-bestun/:routeId" element={<RouteOptimization />} />
            <Route path="abendingar" element={<Feedback />} />
            <Route path="heimasida-myndbond" element={<HeroVideos />} />
            <Route path="greining" element={<Analytics />} />
            <Route path="stefnugreining" element={<StrategicAnalysis />} />
            <Route path="reikni" element={<ROICalculator />} />
            <Route path="simtol" element={<Calls />} />
          </Route>
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
